import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getMessaging, isSupported } from 'firebase/messaging';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { connectDatabaseEmulator, getDatabase } from 'firebase/database';
import { initializeAnalytics, isSupported as isAnalyticsSupported } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
  ignoreUndefinedProperties: true,
});
// When running on *.ulgebra.com, route httpsCallable calls through the same
// origin instead of cloudfunctions.net — avoids CORS and keeps cookies intact.
// Requires a Firebase Hosting rewrite for the function path (firebase.json).
// On any other domain (localhost, preview deploys, etc.) fall back to the
// default cloudfunctions.net endpoint.
const functionsCustomDomain =
  typeof window !== 'undefined' && window.location.hostname.endsWith('.ulgebra.com')
    ? `${window.location.protocol}//${window.location.host}`
    : undefined;
export const functions = getFunctions(app, functionsCustomDomain);
export const storage = getStorage(app);
export const rtdb = getDatabase(app);
// Disable automatic page_view — we log it manually after document.title is set
// Skip analytics in dev; check isSupported() in prod to handle incognito/restricted browsers.
export let analytics: ReturnType<typeof initializeAnalytics> | null = null;
if (typeof window !== 'undefined' && !import.meta.env.DEV) {
  isAnalyticsSupported().then((supported) => {
    if (supported) {
      analytics = initializeAnalytics(app, { config: { send_page_view: false } });
    }
  }).catch(() => {});
}

// FCM — lazy init (not supported in all browsers)
export const getMessagingInstance = async () => {
  const supported = await isSupported();
  return supported ? getMessaging(app) : null;
};

// Emulator support for local dev
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099');
  connectFirestoreEmulator(db, 'localhost', Number(import.meta.env.VITE_FIRESTORE_PORT) || 8080);
  connectFunctionsEmulator(functions, 'localhost', 5001);
  connectDatabaseEmulator(rtdb, 'localhost', 9000)
  connectStorageEmulator(storage, 'localhost', 9199)
}

/**
 * True when this localhost dev client sends its Cloud Function calls to the
 * local emulator (either switch: the .env flag above or the runtime toggle
 * below). Code that only works against the emulator — e.g. the monday OAuth
 * redirect back to localhost, which the live functions ignore — checks this,
 * so plain `npm run dev` against production behaves like production.
 */
export function usesLocalFunctionsEmulator(): boolean {
  if (!import.meta.env.DEV || typeof window === 'undefined' || window.location.hostname !== 'localhost') return false;
  if (import.meta.env.VITE_USE_EMULATORS === 'true') return true;
  try {
    return localStorage.getItem('useFunctionEmulatorDev') === 'true';
  } catch {
    return false;
  }
}

/**
 * monday apps whose "Redirect URLs" (monday developer center) list the
 * localhost URL. Only these may name a localhost redirect_uri in the OAuth
 * round trip: monday refuses a redirect_uri an app has not registered, so
 * naming one for every monday app would break the dev login of all the others.
 * Add an app here after registering `http://localhost:3000/oauth-home?appCode=<app>&client_id=<id>` for it.
 */
const MONDAY_APPS_WITH_LOCAL_REDIRECT = new Set(['recurringtasksformonday']);

/** True when this monday app's OAuth round trip comes back to localhost (dev against the emulator only). */
export function usesLocalMondayRedirect(extensionName: string): boolean {
  return usesLocalFunctionsEmulator() && MONDAY_APPS_WITH_LOCAL_REDIRECT.has(extensionName);
}

// Runtime toggle: set localStorage 'useFunctionEmulatorDev' = 'true' to
// route Cloud Functions through the local emulator (localhost only). Auth,
// Firestore, and Storage stay in production.
// RTDB opts in separately via 'useRtdbEmulatorDev' = 'true': needed only
// when the emulated CFs run against emulated RTDB, so both read/write the
// same instance. With `firebase emulators:start --only functions` the CFs
// talk to PRODUCTION RTDB — the app must too, so leave that flag unset.
if (
  typeof window !== 'undefined' &&
  window.location.hostname === 'localhost' &&
  localStorage.getItem('useFunctionEmulatorDev') === 'true'
) {
  console.log('[Firebase] useFunctionEmulatorDev=true → Cloud Functions routed to localhost:5001 emulator');
  connectFunctionsEmulator(functions, 'localhost', 5001);
  if (localStorage.getItem('useRtdbEmulatorDev') === 'true') {
    connectDatabaseEmulator(rtdb, 'localhost', 9000);
  }
} else if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
  console.log(
    "[Firebase] Cloud Functions using PRODUCTION. To use the local emulator run:\n" +
      "  localStorage.setItem('useFunctionEmulatorDev','true')  then reload."
  );
}

/**
 * Zoho fixed outbound IP. Some Zoho orgs allow API calls only from listed IPs.
 * The `_fixedip` copies of new_callServiceAuthorizedAPI and new_oauthCallbackZoho
 * run the same code but leave Google Cloud from 35.202.240.94.
 * On for users whose ulgebraUsers/{uid} doc has useZohoFixedIp: true (set by
 * auth-store), or in this browser with localStorage 'useZohoFixedIp' = 'true'.
 */
let zohoFixedIpForUser = false;

export function setZohoFixedIpForUser(enabled: boolean): void {
  zohoFixedIpForUser = enabled;
}

// Pending from page load until the user doc read after login is done (2 s max
// once it starts; 5 s max overall). new_callServiceAuthorizedAPI calls wait for
// it, so even the first calls pick the right copy.
let markZohoFixedIpReady: () => void = () => {};
const zohoFixedIpReady = new Promise<void>((resolve) => {
  markZohoFixedIpReady = resolve;
  setTimeout(resolve, 5000);
});

/** Called by auth-store with the user doc read, or with nothing when no one is signed in. */
export function setZohoFixedIpReady(userDocRead?: Promise<unknown>): void {
  if (!userDocRead) {
    markZohoFixedIpReady();
    return;
  }
  userDocRead.then(markZohoFixedIpReady, markZohoFixedIpReady);
  setTimeout(markZohoFixedIpReady, 2000);
}

export function usesZohoFixedIp(): boolean {
  if (zohoFixedIpForUser) return true;
  try {
    return typeof window !== 'undefined' && localStorage.getItem('useZohoFixedIp') === 'true';
  } catch {
    return false;
  }
}

// ~60 call sites name new_callServiceAuthorizedAPI directly, and httpsCallable
// has no per-call URL option. The SDK posts through the global fetch at call
// time, so renaming the path here moves every call to the `_fixedip` copy.
// The flag is checked per call, after the user doc read (see setZohoFixedIpReady).
if (typeof window !== 'undefined') {
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === 'string' && input.endsWith('/new_callServiceAuthorizedAPI')) {
      return zohoFixedIpReady.then(() => originalFetch(usesZohoFixedIp() ? `${input}_fixedip` : input, init));
    }
    return originalFetch(input, init);
  };
}